import React, { useLayoutEffect, useRef } from 'react'
import { Box, Typography } from '@mui/material'
import { Nfc, ChevronRight } from 'lucide-react'
import CircularProgress from '@mui/material/CircularProgress'
import VLogoSVG from 'assets/images/vlogo.svg'
import { VERA_INK, VERA_INK_MUTED, VERA_SURFACE } from 'settings/veraMark'

interface AuthenticChipProps {
  label?: string
  size?: 'small' | 'medium'
  /**
   * VERA-455: whether this certificate was reached via a genuine physical tag
   * scan (a valid `vt`). Certificate data is public; the "Authentic" state is
   * shown only when this is true — a cloned or guessed URL reads as "Not verified"
   * so it can't spoof authenticity. Defaults to `true` so existing non-scan
   * usages are unaffected.
   *
   * Accepts the three-state verdict as well as a boolean. `pending` renders a
   * spinner in the icon slot: the badge RESOLVES IN PLACE rather than the page
   * holding a cover until the verify round-trip returns, so the certificate is
   * readable while the check completes.
   */
  verified?: boolean | 'pending' | 'verified' | 'connected' | 'unverified'
  /**
   * VRG-240: when set, the chip becomes tappable and opens the authenticity
   * explainer. The badge is the only thing on the page asserting the product's
   * core claim, so it needs somewhere to say what that claim actually means --
   * especially in the Unverified state, which reads as an accusation without one.
   */
  onClick?: () => void
  /**
   * VERA-304: this chip FLOATS ON THE PRODUCT PHOTOGRAPH rather than sitting on a card.
   * It takes a solid WHITE fill; the on-card instances keep the grey ramp.
   *
   * ── Why the fill is scoped and not just changed ──────────────────────────────────
   * `AuthenticChip` renders over an image in exactly ONE place: the certificate
   * hero. Its other instance — Provenance's `OwnerProvenanceCard`, via
   * `TransferCompletionChip` — sits on
   * `background.paper`, which IS `#FFFFFF`. A white fill there is a white chip on a
   * white card: it would be legible only by its 1px border, which is the component
   * disappearing, not a restyle. So the white is opt-in per call site.
   *
   * ── Why white BEATS grey[100] here, and why that is the opposite of the earlier answer ──
   * The scrim commit (61a864899) concluded that lightening the chip could not work:
   * "the fill is ALREADY fully opaque, and the chip is LIGHT. Pushing it lighter
   * (toward white, L=1.0) against a pale photo (L≈0.54) reaches 1.77:1 at the
   * theoretical limit, so every value in that direction fails."
   *
   * THAT WAS MEASURED BEFORE THE SCRIM EXISTED, and the scrim is exactly what
   * invalidates it. The scrim darkens the backdrop under the chip, so the ceiling that
   * capped white at 1.77:1 moved. Re-measured post-scrim, worst-pixel edge contrast
   * (chip fill vs the photograph 4px beyond its border), same two photographs the
   * scrim was tuned on:
   *
   * |                        | Cartier (pale) | Omega (darker) |
   * |------------------------|----------------|----------------|
   * | grey[100] — was        | 2.16:1         | 1.84:1         |
   * | grey[50]  — was, verified | 2.46:1      | 2.09:1         |
   * | **white — now**        | **2.71:1**     | **2.30:1**     |
   *
   * +25% on both, and every other metric moves the same way: the label's text contrast
   * goes 16.77:1 → 21.00:1 (the maximum available to black text), and the NFC glyph,
   * which is `text.secondary` and therefore governed by the 3:1 non-text bar rather
   * than 4.5:1, goes 3.43:1 → 4.29:1 — off a floor it was uncomfortably close to.
   *
   * Because the fill stays OPAQUE, all of those hold regardless of the photograph.
   * That is the property translucency would have traded away, and it is why this is
   * not the "liquid glass" treatment the request started as: glass makes the label's
   * contrast a function of whatever the customer photographed.
   *
   * ── Both states, identically ─────────────────────────────────────────────────────
   * Verified and unverified take the SAME fill. A status indicator has to be equally
   * legible in every state — making "Not verified" quieter than "Authentic" would make
   * the less reassuring state the harder one to read, which for an authenticity product
   * is precisely backwards. It would also teach the chrome to carry the verdict, a
   * second and lower-resolution channel competing with the label that already says it.
   * The difference between the states lives in the WORDS.
   *
   * The border needs no equivalent change: `#vera-shadow-root *` in index.css sets
   * `border-color` at ID specificity, so the authored `grey[300]`/`grey[400]` never
   * painted — both states already render `#E4E4E7`. Verified in the browser rather
   * than assumed; see the same trap documented in `OwnerProvenanceCard`.
   */
  onImage?: boolean
}

/** Circle fades and scales up into place. */
const APPEAR_MS = 180
const APPEAR_FROM_SCALE = 0.9
/** Then the pill unfolds rightward. Overshoot, per the design ask for a bounce. */
const EXPAND_MS = 340
const EXPAND_EASING = 'cubic-bezier(0.34, 1.4, 0.64, 1)'
/** Then — and only then — the words arrive. */
const LABEL_MS = 180
/** How far right the label starts, so it drifts LEFT into place as it fades up. */
const LABEL_DRIFT_PX = 5
const LABEL_AT = APPEAR_MS + EXPAND_MS
const ENTRANCE_MS = LABEL_AT + LABEL_MS
/** See `settle` — the timer owns the cleanup; `onfinish` only makes it prompt. */
const SETTLE_GRACE_MS = 80

/**
 * ── THE FOIL SHEEN ───────────────────────────────────────────────────────────────
 * A specular highlight crossing the V and the word, the way light catches the foil
 * on the physical tag. Shares its logic with the brand mark's own sheen stops and
 * the tag preview's logo gloss, so "Vera catching the light" looks like one thing
 * wherever it appears.
 *
 * ── Why ONE unmasked band rather than two shimmering elements ────────────────────
 * `mix-blend-mode: screen` does the masking for free, and does it better than a
 * `mask-image` would. Screen leaves the backdrop untouched wherever the backdrop is
 * already white — `1 - (1-1)(1-b) = 1` for any band value — so on this chip's solid
 * white fill the band is mathematically invisible, and it lightens ONLY the dark
 * pixels: the V glyph and the label. One band, spanning the whole pill, therefore
 * reads as a single light source travelling across the badge. Two separately
 * animated children would read as two things blinking near each other.
 *
 * That also means it needs no knowledge of the glyph: the `mask-image: url(VLogoSVG)`
 * approach the Labs sweep uses is necessary there because that mark sits on a
 * transparent surface. Here the surface is opaque and known.
 *
 * ── The peak alpha is a CONTRAST budget, not a taste call ────────────────────────
 * Screen against black text yields the band's own value, so the brightest point of
 * the sweep IS the label's worst-case colour. At 0.40 white, black text momentarily
 * renders #737373, which is 4.74:1 on white — over the 4.5:1 floor, though with less
 * margin than the 0.40 this started at (5.74:1), and
 * only for the instant the narrow core is over any given letter. 0.5 would reach
 * #808080 at 3.95:1 and fail. Raising it means re-running that arithmetic.
 *
 * ── A REPEATING GLINT WITH A LONG REST, not a burst on arrival ───────────────────
 * This first shipped as three passes that then stopped, and that was wrong for a
 * reason worth recording: the burst fired 860ms after mount, which is exactly the
 * window in which attention is on the 3D model directly above. By the time anyone
 * looked at the badge it was over, permanently — an animation nobody can catch is
 * the same as no animation, minus the code.
 *
 * The argument FOR stopping was that this chip has a `pending` state and perpetual
 * motion on the RESOLVED state would blur checked-vs-checking. That over-weighted
 * it: the pending state is a SPINNER in the icon slot beside the word "Checking…",
 * which nobody will confuse with an occasional glint on a chip that says
 * "Authentic" and carries a V.
 *
 * So it repeats, but the travel occupies only the first 15% of the cycle — 1.35s of
 * movement, then ~7.6s of stillness. That is not "perpetual motion" in the sense the
 * 3D model above it is; it is what foil actually does, which is catch the light
 * occasionally as the hand moves. The rest is what keeps it from competing.
 */
const SHEEN_PASSES = 'infinite'
const SHEEN_MS = 9000
/** The fraction of the cycle spent travelling; the remainder is the rest. */
const SHEEN_TRAVEL_PCT = 26
/** Starts once the entrance has settled, so the two are never animating at once. */
const SHEEN_DELAY_MS = ENTRANCE_MS + 160

/**
 * The badge's entrance: a circle appears, unfolds rightward into the pill, and the label
 * is revealed by the widening rather than arriving with it.
 *
 * ── Why the chip animates ITSELF ─────────────────────────────────────────────────
 * The previous version of this moment was a shared-element transition: the scan loader's
 * circle flew across the screen and expanded into this pill, which meant this component
 * exposed a `morphRef` for an out-of-React module to measure and hide. That flight was
 * rebuilt twice and still read badly, so it is gone (see utils/scanLoader). The gesture
 * it was reaching for — a circle becoming a pill — survives here, at the badge's own
 * position, with no loader involved. The cover just fades.
 *
 * ── Constraints that are load-bearing, not preference ────────────────────────────
 * • The ICON MUST NOT MOVE. The specific complaint about the flight was a glyph riding
 *   the box's centre rightward as it grew. So the box is left-anchored and only its
 *   WIDTH animates: the icon sits at the pill's own padding-left for the entire
 *   entrance, at a viewport x that never changes. The collapsed width is the pill's
 *   HEIGHT (so it is round), which is narrower than the icon plus both paddings — hence
 *   `overflow: hidden` on the box and `flexShrink: 0` on the icon slot, or the flex
 *   layout would squeeze the glyph instead of clipping the label.
 * • It runs ONCE PER MOUNT. The verdict resolving `pending → verified/unverified`
 *   re-renders this component with different contents; re-expanding then would be a
 *   second entrance for a badge that is already on screen, which is worse than none.
 *   `ran` guards it, and the inline width is CLEARED at the end so the chip resizes
 *   naturally when the label text changes underneath it.
 * • Cleanup is owned by a TIMER, not by `onfinish`. A `fill: forwards` animation
 *   outranks inline styles, so a cleanup that never runs leaves the chip frozen as a
 *   circle with an invisible label — permanently. A backgrounded tab freezes the
 *   document timeline (so `onfinish` may never fire) but not `setTimeout`.
 * • `prefers-reduced-motion`, no WAAPI, and an unmeasurable box all render the final
 *   state immediately.
 */
function useChipEntrance(
  root: React.RefObject<HTMLElement | null>,
  label: React.RefObject<HTMLElement | null>
) {
  const ran = useRef(false)

  // Layout effect, not effect: the collapsed width has to be in place before the first
  // paint, or the full pill flashes for a frame before shrinking to a circle.
  useLayoutEffect(() => {
    if (ran.current) return
    ran.current = true

    const el = root.current
    if (!el) return

    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (reduceMotion || typeof el.animate !== 'function') return

    const box = el.getBoundingClientRect()
    const { width: full, height } = box
    const collapsed = height

    // Not laid out, or already round: nothing to unfold, so leave the final state alone.
    if (!full || !height || full <= collapsed + 1) return

    const labelEl = label.current
    const finalRadius = window.getComputedStyle(el).borderRadius

    // Anchor the scale-in on the ICON'S LEFT EDGE rather than the box's centre, so the
    // one thing that must not move doesn't: a centre origin scales the icon 0.9 → 1 about
    // the middle of the pill, which walks its left edge ~0.4px. Small, but it is the exact
    // artefact this rewrite exists to remove, and pinning the origin costs nothing.
    const iconEl = el.firstElementChild
    const originX = iconEl ? iconEl.getBoundingClientRect().left - box.left : 0

    el.style.transformOrigin = `${originX}px 50%`
    el.style.overflow = 'hidden'
    el.style.width = `${collapsed}px`
    el.style.borderRadius = `${collapsed / 2}px`
    if (labelEl) labelEl.style.opacity = '0'

    const animations: Array<Animation | undefined> = []

    let settled = false
    const settle = () => {
      if (settled) return
      settled = true
      // Cancel BEFORE clearing: a filling animation outranks the inline style, so
      // clearing alone would leave the chip wherever the animation froze.
      for (const animation of animations) animation?.cancel()
      el.style.overflow = ''
      el.style.width = ''
      el.style.borderRadius = ''
      el.style.transformOrigin = ''
      if (labelEl) {
        labelEl.style.opacity = ''
        labelEl.style.transform = ''
      }
    }

    // Armed in the same breath as the animations below, for the reason in the doc block:
    // the undo must not depend on the lines that follow it succeeding.
    const timer = setTimeout(settle, ENTRANCE_MS + SETTLE_GRACE_MS)

    try {
      animations.push(
        // 1. The circle arrives.
        el.animate(
          [
            { opacity: 0, transform: `scale(${APPEAR_FROM_SCALE})` },
            { opacity: 1, transform: 'scale(1)' },
          ],
          { duration: APPEAR_MS, easing: 'ease-out', fill: 'backwards' }
        ),
        // 2. …then unfolds rightward. `width` (not `scaleX`) because a scaled box would
        //    stretch the glyph and the border with it; real box geometry stays crisp.
        el.animate(
          [
            { width: `${collapsed}px`, borderRadius: `${collapsed / 2}px` },
            { width: `${full}px`, borderRadius: finalRadius },
          ],
          {
            duration: EXPAND_MS,
            delay: APPEAR_MS,
            easing: EXPAND_EASING,
            fill: 'both',
          }
        )
      )

      // 3. …and the words are revealed by the widening, after it has finished.
      if (labelEl) {
        animations.push(
          labelEl.animate(
            [
              { opacity: 0, transform: `translateX(${LABEL_DRIFT_PX}px)` },
              { opacity: 1, transform: 'translateX(0)' },
            ],
            { duration: LABEL_MS, delay: LABEL_AT, easing: 'ease-out', fill: 'both' }
          )
        )
      }
    } catch {
      clearTimeout(timer)
      settle()
      return
    }

    // Ends the entrance a beat earlier than the timer would. An optimisation, not the
    // guarantee — `settle` is idempotent, so whichever fires first wins.
    const last = animations[animations.length - 1]
    if (last) last.onfinish = settle

    return () => clearTimeout(timer)
  }, [])
}

export const AuthenticChip: React.FC<AuthenticChipProps> = ({
  label,
  size = 'medium',
  verified = true,
  onClick,
  onImage = false,
}) => {
  const rootRef = useRef<HTMLElement | null>(null)
  const labelRef = useRef<HTMLElement | null>(null)

  useChipEntrance(rootRef, labelRef)

  const iconSize = size === 'small' ? 14 : 16
  const padding = size === 'small' ? { px: 1, py: 0.375 } : { px: 1.25, py: 0.5 }
  const state: 'pending' | 'verified' | 'connected' | 'unverified' =
    verified === true ? 'verified'
      : verified === false ? 'unverified'
        : (verified ?? 'verified')
  const isVerified = state === 'verified'
  const isPending = state === 'pending'
  /**
   * VRG-252 — `connected`: a Connect (NTAG 213) tag WAS tapped, and its silicon carries no
   * challenge to answer, so nothing about the tap distinguishes the original chip from a copy of
   * its address.
   *
   * THE LABEL STATES THE OBSERVED FACT AND CLAIMS NOTHING BEYOND IT. "Authentic" is the one word
   * it must never borrow. "Connect" was rejected too: that is the internal capability name, which
   * a person holding the item has never seen and would read as a verdict rather than an event.
   *
   * IT SHARES THE UNVERIFIED STATE'S NEUTRAL TREATMENT — grey ramp, NFC glyph, no V mark —
   * DELIBERATELY, and this is the choice worth defending. The distinction that must never blur is
   * connected-versus-verified: the V mark means a proof was checked, and lending it to a tag that
   * cannot produce one is the whole failure this task exists to prevent. Connected-versus-
   * unverified is the lesser distinction, and sharing the quieter treatment errs AWAY from
   * confidence, which is the safe direction to be wrong in. The words carry that difference, and
   * the drawer explains it.
   */
  const isConnected = state === 'connected'
  const resolvedLabel =
    label ??
    (isPending ? 'Checking…' : isVerified ? 'Authentic' : isConnected ? 'Tag scanned' : 'Not verified')
  const interactive = !!onClick

  return (
    <Box
      // Real button semantics rather than a click handler on a div: this is the
      // pattern StatusBadge already established (VERA-628) -- keyboard activation
      // and a visible focus ring, not just a tap target.
      component={interactive ? 'button' : 'div'}
      ref={rootRef}
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      aria-label={interactive ? `${resolvedLabel}. What does this mean?` : undefined}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        ...padding,
        borderRadius: '16px',
        /* All three exist for the sheen overlay below.
           - `relative` anchors it.
           - `hidden` keeps the band inside the pill. It does NOT clip the focus
             ring: that is a box-shadow on this element, and overflow clips
             descendants, not the element's own shadow. The chevron's 3px hover
             travel also stays inside the 10px padding-right.
           - `isolate` confines the blend group to the chip, so `screen` can never
             reach past the opaque fill into the photograph behind it.
           `useChipEntrance` sets `overflow` inline during the entrance and clears
           it afterwards, which correctly falls back to this value rather than to
           `visible`. */
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        // NEUTRAL, deliberately not amber. Arriving without a tag scan is the ordinary
        // case — a shared or saved link — and the drawer says so in as many words. An
        // amber "warning" pill would contradict our own copy and grade the ITEM as
        // suspect when all we actually know is how the page was opened.
        border: theme => `1px solid ${isVerified ? theme.palette.grey[300] : theme.palette.grey[400]}`,
        width: 'fit-content',
        // Solid white ONLY over the photograph, both states alike; the grey ramp
        // everywhere else. `onImage`'s docblock carries the measurements.
        backgroundColor: theme =>
          onImage
            ? VERA_SURFACE
            : isVerified ? theme.palette.grey[50] : theme.palette.grey[100],
        ...(interactive && {
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
          /*
            HOVER MOVES THE CHEVRON, AND MOVES ONLY THE CHEVRON.

            The chip had `cursor: pointer` and a focus ring but no hover state at
            all, so on a mouse the only signal it opens anything was the cursor —
            which you must already be on top of to see.

            A first pass ALSO darkened the background a step. That was wrong: the
            chip is already grey, so a grey-on-grey shift is a weak signal that
            competes with the strong one instead of supporting it, and it draws
            the eye to the whole pill rather than to the thing that says "there is
            more this way". The travel is the message; the fill is noise.

            3px rather than 2 because the chevron is now carrying hover ALONE —
            with a background shift beside it 2px was enough, without one it is
            too easy to miss. Still small: on a 30px chip, travel big enough to
            read as motion in isolation reads as the control wobbling.

            Gated on `@media (hover: hover)` because on touch `:hover` STICKS after
            a tap — the chip would hold its hovered state until you tapped
            elsewhere, which reads as a stuck control. Phones open the drawer
            immediately and never needed the invitation.
          */
          '@media (hover: hover)': {
            '&:hover': {
              '& [data-chip-chevron]': { transform: 'translateX(3px)', opacity: 1 },
            },
          },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: theme => `0 0 0 3px ${theme.palette.grey[400]}`,
            '& [data-chip-chevron]': { transform: 'translateX(2px)', opacity: 1 },
          },
        }),
      }}
    >
      {/* `flexShrink: 0` on every slot is what keeps the icon its own size while the box
          is narrower than its contents during the entrance — see useChipEntrance. */}
      {isPending ? (
        <Box
          sx={{ width: iconSize, height: iconSize, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <CircularProgress size={iconSize - 2} thickness={5} sx={{ color: VERA_INK_MUTED }} />
        </Box>
      ) : isVerified ? (
        <Box
          component="img"
          src={VLogoSVG}
          alt="Verified"
          sx={{ width: iconSize, height: iconSize, flexShrink: 0 }}
        />
      ) : (
        /* An NFC mark: the REMEDY, not the cause and not a verdict.

           Two glyphs have been rejected here and the reasons still hold. A
           `ShieldAlert` said "this might be fake" while the drawer it opens says a
           shared link is normal — the icon was grading the item, and lying. A
           `Link2` fixed that by naming the cause, but a chain link is the
           universal glyph for "hyperlink", so on a page that also offers Share and
           Copy link it read as a link ACTION sitting in a status pill.

           This names what closes the gap: tap the tag. It is neutral about the
           object, it matches the physical gesture the whole product is built on,
           and it pairs with the verified state's V mark instead of competing with
           the red shield the invalid-scan surfaces already own.

           ── WHY `connected` AND `unverified` STILL SHARE ONE GLYPH ──────────────
           Giving `unverified` its own mark was tried and REJECTED ON MEASUREMENT,
           so that the next person does not re-run the experiment.

           The idea was sound and the constraint it respected is the right one:
           differentiate on ABSENCE, not fault — same wave, rendered incomplete —
           because "Not verified" is not an accusation and any alarm glyph
           (triangle, shield, exclamation) re-commits the `ShieldAlert` mistake in
           a new costume. Eight variants were built and viewed at the real 14px and
           16px: 3-arc, 2-arc, 1-arc, dashed outer, dotted outer, faded outer,
           faded outer-two, and thinner stroke.

           None of them survives at size, and the geometry says why. The lucide
           `Nfc` wave is FOUR arcs at viewBox x=6/9.46/12.91/16.37, so at the 16px
           this chip actually renders the entire mark is 7.9px wide (6.9px at 14px),
           its stroke is 1.0px (0.875px at 14px), and neighbouring arcs sit 2.3px
           apart. A dash pattern therefore has to live inside a 1px stroke on an arc
           2px from its neighbour — it renders as a faint smudge, not as "broken".
           Opacity fades do the same thing for the same reason.

           The count changes survive rendering but fail for a different and more
           interesting reason: dropping one arc moves the mark's width by 2.3px, and
           these two states are MUTUALLY EXCLUSIVE — one certificate shows one chip.
           Nobody has the other glyph on screen to compare against, and nobody
           memorises that the NFC wave has four arcs. A distinction that is only
           legible in an A/B strip conveys nothing in production.

           So the LABEL carries the difference ("Tag scanned" vs "Not verified"),
           which it does unambiguously, and the glyph stays a consistent marker for
           "this is about the tag". If this needs revisiting, the lever is a
           different channel — wording, or the drawer — not a smaller glyph detail. */
        <Box
          component={Nfc}
          // VRG-252: was the literal "Not verified", which a screen reader then announced for a
          // `connected` chip whose visible label says the opposite. The glyph is shared by both
          // states, so its label has to follow the resolved one.
          aria-label={resolvedLabel}
          sx={{ width: iconSize, height: iconSize, flexShrink: 0, color: VERA_INK_MUTED }}
          strokeWidth={1.5}
        />
      )}
      <Typography
        ref={labelRef}
        variant="caption"
        sx={{
          /* ONE colour for every verdict, deliberately — this was `verified ?
             'text.primary' : 'text.secondary'`.

             That condition was broken in a way that hid itself: `verified` carries the
             STRING 'unverified' in one state, which is truthy, so both states already
             rendered `text.primary`. What shipped was correct; the condition merely
             was not doing what it read as.

             So the fix is NOT to repair the operand to `isVerified`. That would have
             made the condition finally work and, in working, would have introduced the
             regression — "Not verified" dropping to grey while "Authentic" stayed
             black.

             A status indicator must be equally legible in every state. Making the LESS
             reassuring verdict the quieter one is backwards for an authentication
             product: it is the shape of a system that flatters. It is also the same
             error as the rejected `ShieldAlert` glyph, inverted — that one over-claimed
             fault, this would under-claim absence.

             The difference between verdicts belongs to the LABEL, which already carries
             it. Same reasoning as the fill: both states are white (see `onImage`).

             VRG-827: the colour is now VERA_INK rather than `text.primary`, and that is
             a bug fix, not a restyle. `text.primary` IS the tenant's Studio
             `text_color`, so a brand with light text rendered this label WHITE ON THE
             WHITE FILL — measured at a contrast ratio of exactly 1.000, with the glyph
             (`text.secondary`, same derivation) at about 1.5:1. The chip read as an
             empty capsule. See `settings/veraMark.ts`: pin the whole mark or none of
             it, because half-pinned is worse than either. */
          color: VERA_INK,
          fontWeight: 600,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          fontSize: size === 'small' ? '0.7rem' : undefined
        }}
      >
        {resolvedLabel}
      </Typography>
      {/* Visible tap affordance. The badge has been tappable since VRG-240, but the
          invitation existed only in an aria-label — so for sighted users the drawer was
          reachable only by guessing. That was survivable while a separate "Not verified
          in this session" callout carried the explanation; removing that callout makes
          this the ONLY route to it, so the affordance has to be visible. */}
      {interactive && (
        <Box
          component={ChevronRight}
          aria-hidden
          data-chip-chevron
          sx={{
            width: iconSize - 2,
            height: iconSize - 2,
            flexShrink: 0,
            color: VERA_INK_MUTED,
            opacity: 0.7,
            ml: -0.25,
            /* ABOVE the sheen, so the band never touches it.
               `mix-blend-mode: screen` blends with what is painted BELOW it, so
               raising this out of the overlay's backdrop is all it takes. The
               chevron is navigation chrome rather than brand — it says "there is
               more this way", and foil catching a wayfinding arrow is the badge
               drawing attention to the wrong thing. The V and the word are the
               VERA mark; those are what the foil belongs to.

               `position` is required, not decoration: `z-index` is ignored on a
               statically-positioned element, so without it the chevron stays in
               the overlay's backdrop and the band lifts it anyway. */
            position: 'relative',
            zIndex: 2,
            transition: 'transform 160ms ease, opacity 160ms ease',
            /* Someone who has asked the OS to stop moving things still gets the
               opacity lift, which carries the same "this is live" message without
               the travel. */
            '@media (prefers-reduced-motion: reduce)': { transition: 'opacity 160ms ease' },
          }}
          strokeWidth={2}
        />
      )}
      {/* VERIFIED ONLY. The foil is the V mark's own material, and the unverified
          and connected states deliberately carry no V — a glint over "Not verified"
          would be chrome celebrating the absence of a verdict.

          This is not the thing the `onImage` docblock argues against. That rule is
          about LEGIBILITY: neither state may be made harder to read than the other,
          which is why both take the same fill and the same ink. Adding a flourish to
          one state takes nothing away from the other, and the chrome already differs
          by verdict anyway — the glyph itself is a V here and an NFC wave there. */}
      {isVerified && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            pointerEvents: 'none',
            /* Narrow bright core, angled across the pill. A wide soft gradient
               reads as the badge changing colour rather than as light moving over
               it; the 100deg tilt is what makes it cross the letterforms the way a
               real highlight would. Peak 0.40 — see SHEEN_MS's docblock for the
               contrast arithmetic that fixes that number. */
            /* WIDER THAN THE FIRST BUILD, which was measured as imperceptible.
               The core spanned 47%-53% of a ~120px chip: a 7px bright band crossing
               12px text, lighting each letter for roughly 80ms. The animation was
               confirmed RUNNING (`currentTime` advancing 1208ms in 1200ms) while
               still being invisible to the eye, which is the definition of too
               subtle. The lit region is now ~40% of the chip, so most of the word is
               affected at once and the lift has time to register. */
            backgroundImage: `linear-gradient(100deg,
              rgba(255,255,255,0) 20%,
              rgba(255,255,255,0.22) 36%,
              rgba(255,255,255,0.45) 50%,
              rgba(255,255,255,0.22) 64%,
              rgba(255,255,255,0) 80%)`,
            mixBlendMode: 'screen',
            '@keyframes authenticChipSheen': {
              /* Travel, then REST. The stop at `SHEEN_TRAVEL_PCT` is what makes
                 successive glints read as separate events rather than a conveyor
                 belt, and it is the whole reason this can repeat without becoming
                 the second perpetual motion in the viewport. */
              '0%': { transform: 'translateX(-120%)' },
              [`${SHEEN_TRAVEL_PCT}%`]: { transform: 'translateX(120%)' },
              '100%': { transform: 'translateX(120%)' },
            },
            /* A CSS animation, not WAAPI or rAF — the one timeline the Claude Code
               preview pane still advances, so this can be verified by scrubbing
               rather than only by reading the source. Same reasoning the Ask panel's
               `shimmerSweep` records. */
            animation: `authenticChipSheen ${SHEEN_MS}ms cubic-bezier(0.4, 0, 0.25, 1) ${SHEEN_DELAY_MS}ms ${SHEEN_PASSES}`,
            /* Someone who asked the OS to stop moving things gets the plain badge.
               Nothing is lost: the sheen is decoration over a label that already
               says everything the chip means. */
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              backgroundImage: 'none',
            },
          }}
        />
      )}
    </Box>
  )
}
